/**
 * **플레이스 운영지역·기준 거리·자동 편입의 어휘와 계산 한 곳** — 의존성이 없는 순수 모듈이다.
 *
 * 서버(`GET /place/{id}` · `GET /place/{id}/auto-enroll/preview` · `.../runs`)는 **어휘만** 내린다:
 * 사유는 `ALREADY_MEMBER`·`NOT_IN_AREA` 같은 코드, 지역 판정 근거는 `CODE|NAME|NONE`, 플레이스 단위
 * 건너뜀은 `NO_SERVICE_AREA` 다. 한글 문구는 프론트가 만든다 — 배달 메뉴 동기화(`deliveryMenuNotes.js`)·
 * 외부 채널 연동(`partnerLink.js`)과 같은 결이고, 그 매핑을 화면마다 적으면 같은 코드의 문구가 갈린다.
 *
 * 운영지역 편집의 계산(주소 위젯 결과 → 지역 후보, 중복·포함 판정, 표시 문자열)도 여기다. 판정 규칙은
 * 서버 `PlaceRegionRules` 와 **같은 식**이다: 시군구 5자리가 지역 코드이고, 특정시 행정구의 부모는
 * 앞 4자리 + `0` 이다(수원시 영통구 `41117` → 수원시 `41110`). 시군구 코드 표는 **화면에 들이지 않는다** —
 * 값의 원천은 주소 위젯 하나다(`utils/addressPicker.js`).
 *
 * 그래서 이 파일은 **의존성 없는 순수 모듈만** 읽는다(`./without.js`). 앱 번들 밖(node 내장 러너)에서
 * 그대로 읽혀야 하므로 Vue·스토어·`@/` 별칭은 들이지 않고, 상대 import 에 `.js` 확장자를 적는다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/B7a-플레이스화면-adminweb-brief.md`,
 * 서버 `B6-자동편입-appserver-report.md` §5·§13·§14 · `B6-2-기준주소-거리-appserver-brief.md`.
 */

import { without } from './without.js'

/** 행정표준코드의 시군구 자릿수 — 서버 `PlaceRegionRules.SIGUNGU_LENGTH` 와 같은 값 */
export const SIGUNGU_LENGTH = 5

/**
 * 최대 배달거리의 범위 — 서버 `PlaceServiceAreaRules.DISTANCE_MIN`·`DISTANCE_MAX` 와 **같은 값**이다.
 *
 * 서버는 범위를 넘으면 400(「maxDeliveryKm 은 1~100 이어야 합니다.」)을 내는데 그 400 은 **필드 오류가
 * 아니라 메시지뿐**이라 폼이 어느 칸인지 말하지 못한다.
 * 그래서 입력칸이 먼저 막고, 이미 저장된 값은 **클램프하지 않는다** — 화면이 사람 몰래 값을 고치면
 * 「저장한 적 없는 값이 저장된다」가 된다. 못 넣을 값이 들어 있으면 말만 한다([validMaxDeliveryKm]).
 */

// 확장자를 적는 이유는 이 파일 맨 위 주석 그대로다(node ESM 은 확장자를 추론하지 않는다)
import { toNumberOrNull } from './numberValue.js'
// 콜팝 참여 조건 미달의 사유 문구는 매장 입점 현황의 것을 그대로 쓴다([candidateEntryView])
import { joinLockReason } from './callpopOrg.js'

export const MAX_DELIVERY_KM_MIN = 1
export const MAX_DELIVERY_KM_MAX = 100

/**
 * 미리보기 한 묶음이 **그리는** 행 수의 상한 — 건수는 이 상한과 무관하게 전부 센다([cappedRows]).
 *
 * `el-collapse-item` 은 접혀 있어도 슬롯을 그리고 `el-table` 에는 가상 스크롤이 없다. 서버는 `items` 를
 * 자르지 않으므로(보고서 §5) 매장 수천 곳짜리 플레이스에서 다이얼로그 하나가 그만큼의 노드를 만든다.
 * `adminRole.PLACE_OPTION_MAX` 와 같은 결의 상한이다 — 근본은 서버 페이징이고, 그때 이 상한이 사라진다.
 */
export const PREVIEW_ROW_MAX = 50

/** 운영지역 항목 하나를 넣지 않은 이유 — 서버 `PlaceAutoEnrollPolicy.Reason` 과 같은 어휘 */
export const AUTO_ENROLL_REASON = Object.freeze({
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  MANUALLY_REMOVED: 'MANUALLY_REMOVED',
  NOT_IN_AREA: 'NOT_IN_AREA',
  UNRESOLVED_ADDRESS: 'UNRESOLVED_ADDRESS',
  NO_GPS: 'NO_GPS',
  OUT_OF_RANGE: 'OUT_OF_RANGE',
  /** 콜팝 참여 조건(PG 설정·판매 메뉴) 미달 — 편성 후보의 [PLACE_ENTRY.INELIGIBLE] 과 같은 판정(R-N) */
  INELIGIBLE: 'INELIGIBLE',
  TV_NOT_SUBSCRIBED: 'TV_NOT_SUBSCRIBED',
})

/**
 * 사유 → 한글 라벨. **순서가 서버의 사유 우선순위**다(B6-2 브리프 「정책」):
 * `ALREADY_MEMBER → MANUALLY_REMOVED → 지역 → 거리 → 참여 조건 → TV`. 화면의 묶음도 이 순서로 그린다.
 * 참여 조건이 TV 앞인 이유는 서버 편성 판정(`PlaceEntryPolicy.entry`)과 같은 순서라서다(R-N).
 */
export const AUTO_ENROLL_REASON_NM = Object.freeze({
  [AUTO_ENROLL_REASON.ALREADY_MEMBER]: '이미 편성됨',
  [AUTO_ENROLL_REASON.MANUALLY_REMOVED]: '관리자가 뺀 매장',
  [AUTO_ENROLL_REASON.NOT_IN_AREA]: '운영지역 밖',
  [AUTO_ENROLL_REASON.UNRESOLVED_ADDRESS]: '주소를 읽지 못함',
  [AUTO_ENROLL_REASON.NO_GPS]: '매장 좌표 없음',
  [AUTO_ENROLL_REASON.OUT_OF_RANGE]: '기준 거리 밖',
  [AUTO_ENROLL_REASON.INELIGIBLE]: '콜팝 참여 조건 미달',
  [AUTO_ENROLL_REASON.TV_NOT_SUBSCRIBED]: '우리동네TV 미입점',
})

/** 플레이스 하나를 통째로 건너뛴 이유 — 서버 `PlaceAutoEnrollPolicy.PlaceSkip` + 경계 사유 둘 */
export const PLACE_SKIP_NM = Object.freeze({
  PLACE_CLOSED: '중지된 플레이스라 자동 편입이 돌지 않습니다.',
  NO_SERVICE_AREA: '운영지역이 설정되지 않아 자동 편입이 돌지 않습니다. (「전국」과 같은 뜻으로 읽지 않습니다)',
  AREA_UNUSABLE: '운영지역 항목의 코드를 읽을 수 없어 자동 편입이 돌지 않습니다. 지역을 다시 지정해 주세요.',
  PLACE_NOT_FOUND: '플레이스를 찾을 수 없습니다.',
  AUTO_ENROLL_OFF: '자동 편입이 꺼져 있습니다.',
  /** 소유 축(M3 리얼 선결) — 자동 편입은 「콜팝 참여 매장을 콜팝 플레이스에」 넣는 기능이다 */
  NOT_CALLPOP_OWNED: '콜팝 소유 플레이스가 아니라 자동 편입이 돌지 않습니다.',
})

/** 무엇으로 지역을 판정했는가 — 서버 `PlaceRegionRules.By` */
export const REGION_BY_NM = Object.freeze({
  CODE: '법정동 코드',
  NAME: '주소 표기',
  NONE: '조건 없음',
})

/** 실행 모드 — 서버 `PlaceAutoEnrollMode` */
export const RUN_MODE = Object.freeze({ DRY_RUN: 'DRY_RUN', APPLY: 'APPLY' })

export const RUN_MODE_NM = Object.freeze({
  DRY_RUN: '미리 돌려보기',
  APPLY: '실제 편입',
  OFF: '꺼짐',
})

/** 무엇이 실행을 걸었는가 — 서버 `PlaceAutoEnrollTrigger` */
export const RUN_TRIGGER_NM = Object.freeze({
  MANUAL: '수동',
  CRON: '자동(배치)',
})

/** [addArea] 의 답 — 화면은 이 값으로 안내 문구를 고른다 */
export const ADD_AREA_RESULT = Object.freeze({
  ADDED: 'ADDED',
  /** 더한 지역이 이미 있는 하위 구를 품는다 — 더하되 그 사실을 말한다 */
  ADDED_COVERING: 'ADDED_COVERING',
  /** 같은 코드가 이미 있다 */
  DUPLICATE: 'DUPLICATE',
  /** 상위(시 전체)가 이미 있어 이 구는 이미 포함된다 */
  COVERED: 'COVERED',
  /** 위젯이 시군구 코드를 주지 않았다 */
  INVALID: 'INVALID',
})

/** 화면 문구 한 곳 — 컴포넌트 안에 한글을 다시 적지 않는다 */
export const PLACE_AREA_TEXT = Object.freeze({
  nationwideLabel: '전국',
  nationwideHint: '전국을 켜면 지역 조건 없이 콜팝 참여 매장 전부가 후보가 됩니다.',
  /**
   * 전국으로 **저장하면** 지역 목록이 남지 않는다 — 서버 `PlaceServiceAreaRules.validate` 가 `nationwide`
   * 면 `areas` 를 버리고 `serialize` 한다. 폼 안에서는 끄면 돌아오지만([setNationwide]) 저장을 지나면
   * 지역 0곳이고, 그 상태로 전국을 끄면 자동 편입이 통째로 건너뛴다(`NO_SERVICE_AREA`).
   */
  nationwideDropsAreas: '전국으로 저장하면 지정한 지역은 지워집니다',
  emptyAreas: '지정된 지역이 없습니다. 「주소로 지역 추가」로 운영지역을 정하세요.',
  emptyServiceArea: '운영지역이 설정되지 않았습니다.',
  addByAddress: '주소로 지역 추가',
  pickScopeTitle: '어느 범위로 추가할까요?',
  duplicate: '이미 추가된 지역입니다.',
  invalid: '이 주소에서는 시군구 코드를 읽지 못했습니다. 다른 주소로 시도해 주세요.',
  autoEnrollNeedsArea: '운영지역을 먼저 지정해야 자동 편입을 켤 수 있습니다.',
  autoEnrollOnHint: '매일 새벽 배치가 운영지역·콜팝 참여·우리동네TV 입점을 보고 매장을 편성합니다.',
  autoEnrollOffHint: '자동 편입이 꺼져 있어 매장은 손으로만 편성됩니다.',
  baseAddressTitle: '기준 주소',
  baseAddressHint: '자동 편입의 거리 조건은 기준 주소와 최대 거리가 둘 다 있을 때만 켜집니다.',
  baseAddressClear: '지우기',
  baseAddressEmpty: '기준 주소가 없습니다. (거리 조건 꺼짐)',
  maxDeliveryKmLabel: '최대 거리(km)',
  /**
   * 최대 거리는 두 곳에 쓰인다 — 고객이 보는 플레이스 목록(고객 위치에서 이 거리 안의 매장만, 목록 SP v4)과
   * 자동 편입의 거리 조건(기준 주소가 함께 있을 때만 — [baseAddressHint]).
   */
  maxDeliveryKmHint: `고객 위치에서 이 거리 안의 매장만 목록에 나옵니다. 비우면 거리로 거르지 않습니다. (${MAX_DELIVERY_KM_MIN}~${MAX_DELIVERY_KM_MAX}km)`,
  maxDeliveryKmOutOfRange: `저장된 값이 ${MAX_DELIVERY_KM_MIN}~${MAX_DELIVERY_KM_MAX}km 밖입니다. 다시 입력해야 저장됩니다.`,
  slugLocked: 'URL명은 등록 뒤 바꿀 수 없습니다.',
  ownerPartnerNone: '없음 — 편성 통지 미발송',
  /**
   * 등록에서 소유를 비운 값의 뜻 — **호출자의 세계가 정한다**(서버 `PlacePartnerRules.defaultOwnerOf`).
   *
   * 종전 문구는 「비우면 콜팝 소유로 등록됩니다」였고 결정 31 당시에는 참이었다. M3 리얼 선결 회차가
   * 그 폴백을 없앴으므로(서버 보고서 §2-1 ③) 지금 내부 관리자가 비우고 저장하면 **소유 없음**이다 —
   * 옛 문구를 그대로 두면 화면이 일어나지 않을 일을 약속한다.
   */
  ownerPartnerNoneNew: '비우면 소유 없이 등록됩니다 (편성 통지 미발송)',
  /**
   * 같은 사실을 **고를 수 없는 사람**에게 말하는 문장 — 「비우면」은 그 사람이 할 수 있는 선택이 아니다.
   *
   * 소유 파트너는 오더엔 내부 관리자만 고르고(`canManagePlaceOwnerPartner`), 등록 폼의 사업장 관리자는
   * 선택 상자를 아예 못 본다. 같은 함수의 콜팝 갈래([ownerPartnerCallpopNew])가 이미 못 고르는 사람의
   * 목소리라 그 비대칭이 이 문장이 빠져 있었다는 근거다.
   */
  ownerPartnerNoneNewReadOnly: '소유 없이 등록됩니다 (편성 통지 미발송)',
  /** 콜팝 역할이 만드는 플레이스는 소유를 비워도 서버가 콜팝으로 각인한다(같은 규칙의 다른 갈래) */
  ownerPartnerCallpopNew: '콜팝 소유로 등록됩니다',
  /**
   * 소유를 **옮기는** 저장의 확인 — 서버가 같은 트랜잭션에서 옛 소유 파트너의 운영자 연결을 전부 끊고(결정 35),
   * 소유를 되돌려도 그 연결은 돌아오지 않는다(이력에는 남는다).
   */
  ownerPartnerTransferTitle: '소유 파트너 변경',
  ownerPartnerTransferConfirm:
    '소유 파트너를 바꾸면 기존 소유 파트너의 운영자 연결이 모두 해제되며, 소유를 되돌려도 복구되지 않습니다. 계속할까요?',
  ownerPartnerDenied: '소유 파트너는 오더엔 내부 관리자만 바꿀 수 있습니다.',
  ownerPartnerLoadFailed: '파트너 목록을 읽지 못해 현재 값만 보여 줍니다.',
  runApplyConfirm: '지금 실제로 편입합니다. 편입된 매장은 편성 이력에 남습니다. 계속할까요?',
  previewEmpty: '지금 배치가 돌면 새로 편입될 매장이 없습니다.',
  previewUsesSaved: '아래 미리보기는 저장된 설정 기준입니다.',
  previewLoadFailed: '미리보기를 읽지 못했습니다.',
  runFailed: '자동 편입을 실행하지 못했습니다.',
  runsEmpty: '실행 기록이 없습니다.',
  runsLoadFailed: '실행 기록을 읽지 못했습니다.',
  retry: '다시 시도',
  needSaveFirst: '플레이스를 먼저 저장하면 미리보기와 실행 기록을 볼 수 있습니다.',
  /**
   * 미리보기도 배치도 **서버에 저장된 설정**으로 돈다. 같은 다이얼로그에서 운영지역·기준 주소·최대 거리를
   * 고친 직후 켜거나 실행하면, 사람이 확인한 목록과 실제 대상이 달라진다([autoEnrollInputsChanged]).
   */
  unsavedBeforeAutoEnroll: '운영지역·기준 주소·최대 거리를 바꿨습니다. 먼저 저장한 뒤 자동 편입을 켜 주세요.',
  unsavedBeforeRun: '운영지역·기준 주소·최대 거리를 바꿨습니다. 먼저 저장한 뒤 실행해 주세요.',
  /** 「켜기」의 선행 조건 — 미리보기를 읽지 못한 채로는 무엇이 들어갈지 아무도 모른다 */
  previewRequiredBeforeOn: '미리보기를 읽은 뒤에 켤 수 있습니다. 「다시 시도」로 미리보기를 먼저 읽어 주세요.',
  /** 자동 편입 확인창의 첫 줄 — 컴포넌트 안에 한글을 적지 않는다(종전에는 템플릿에 박혀 있었다) */
  autoEnrollConfirmHint:
    '켜면 매일 새벽 배치가 운영지역·콜팝 참여·우리동네TV 입점을 보고 아래 매장을 편성합니다. 「켜기」는 폼의 값만 바꾸며, 저장해야 적용됩니다.',
  /**
   * 후보 0건의 답 — **확인된 사실만 말하고 원인은 단정하지 않는다**([availableStoresEmptyText]).
   *
   * 빈 후보 목록이 증명하는 것은 「지금 이 조건으로 추가할 수 있는 매장이 없다」 하나뿐이다. 마지막
   * 후보를 방금 추가했거나(`addStore` 가 그 행을 목록에서 뺀다) 검색어·지역·입점 필터가 걸려 있어도
   * 같은 0건이 되므로, 「콜팝 참여를 먼저 등록하세요」 같은 조치를 지목하면 화면이 **일어나지 않은
   * 일을 원인으로 단정**한다(참여 매장이 이미 다 편성된 플레이스가 그 문장을 보게 된다).
   *
   * 그래서 사실 한 줄 + **확인할 곳의 목록**으로 말한다. 목록은 지금 실제로 걸려 있는 축만 든다.
   */
  availableEmpty: '지금 조건으로 추가할 수 있는 매장이 없습니다.',
  availableEmptyCheckPrefix: '확인할 것',
  availableEmptyCheckFilter: '검색어·지역 조건',
  /** 콜팝 축이 아니라 우리동네TV 축이라 어느 플레이스에서도 후보를 줄인다 */
  availableEmptyCheckSubscribed: '「우리동네 입점 매장만」 토글',
  /** 콜팝 소유 플레이스의 후보는 「콜팝 참여 ACTIVE ∩ 내 가시 범위」라 참여 원장이 후보를 정한다 */
  availableEmptyCheckCallpop: '매장의 콜팝 참여 등록',
  /** 「못 읽었다」와 「없다」는 다른 답이다(자동 편입 패널의 같은 규율) */
  availableLoadFailed: '매장 후보를 읽지 못했습니다.',
  storesSaveFailed: '편성을 저장하지 못했습니다.',
  /** 편성 후보 뱃지([candidateEntryView]) — 막힌 판정(참여 조건 미달·거리 밖·다른 일반 플레이스)은 모두 같은 뱃지다 */
  entryAddable: '추가가능',
  entryBlocked: '추가불가',
  /**
   * 거리 밖(`OUT_OF_RANGE`)의 사유 줄 — 거리 축이 켜졌고 잰 거리가 최대 배달거리를 넘었다.
   * 자동 편입 패널의 같은 판정과 같은 말을 한 곳([AUTO_ENROLL_REASON_NM])에서 쓴다. 「추가불가」는 뱃지가 말한다.
   */
  entryOutOfRange: AUTO_ENROLL_REASON_NM[AUTO_ENROLL_REASON.OUT_OF_RANGE],
  /**
   * 같은 `OUT_OF_RANGE` 이되 매장 좌표가 없어 재지 못했다(`distanceKm` null) — 사람이 할 일(매장 좌표 고치기)이 달라
   * 자동 편입 패널·서버 400 과 같은 「매장 좌표 없음」으로 말한다.
   */
  entryNoGps: AUTO_ENROLL_REASON_NM[AUTO_ENROLL_REASON.NO_GPS],
  /** 후보 행의 거리([candidateDistanceText]) — 기준점은 플레이스의 기준 주소다 */
  candidateDistancePrefix: '기준 주소에서',
  /** 판정이 없는 행 — 이 다이얼로그에서 뺀 등록 매장이 후보 맨 위로 되돌아온 것이다 */
  entryRestored: '등록 매장 · 되돌리기',
})

/** 빈 운영지역 — `nationwide` 도 `areas` 도 없는 상태 */
export function initServiceArea() {
  return { nationwide: false, areas: [] }
}

/**
 * 서버가 준 값(또는 `null`) → 편집기가 다루는 모양. 서버는 미설정을 `null` 로 내리고 화면은 빈 객체로
 * 다룬다 — 「전국」과 「미설정」을 섞지 않으려면 둘의 모양이 같아야 한다([toServiceAreaPayload] 가 되돌린다).
 */
export function normalizeServiceArea(raw) {
  if (!raw || typeof raw !== 'object') return initServiceArea()

  const areas = Array.isArray(raw.areas) ? raw.areas : []

  return {
    nationwide: raw.nationwide === true,
    areas: areas
      .filter((area) => area && area.code)
      .map((area) => ({ code: String(area.code), name: area.name || '' }))
      .filter((area, index, all) => all.findIndex((other) => other.code === area.code) === index),
  }
}

/**
 * 편집기 값 → 저장 본문의 `serviceArea`. **미설정은 `null`** 이다(서버 델타 브리프: `null` = 지움).
 *
 * `nationwide` 가 꺼져 있고 지역도 없으면 미설정이다 — 빈 `areas` 를 보내면 서버가 「지역 0곳」이라는
 * 다른 뜻으로 읽을 자리를 만든다.
 *
 * **전국이면 `areas` 를 싣지 않는다** — 서버 `PlaceServiceAreaRules.validate` 가 `nationwide` 일 때
 * `ServiceArea(true, emptyList())` 로 접어 저장하기 때문이다. 화면이 지역을 실어 보내면 본문과 저장된
 * 값이 갈리고, 저장 뒤 전국을 끈 사람은 자기가 지정했던 지역이 사라진 것을 **저장 다음에야** 본다.
 * 보낸 모양이 곧 저장될 모양이어야 [autoEnrollInputsChanged] 같은 「바뀌었나」 판정도 참말을 한다.
 */
export function toServiceAreaPayload(serviceArea) {
  const value = normalizeServiceArea(serviceArea)
  if (!value.nationwide && value.areas.length === 0) return null
  if (value.nationwide) return { nationwide: true, areas: [] }

  return { nationwide: false, areas: value.areas.map((area) => ({ code: area.code, name: area.name })) }
}

/** 자동 편입을 켤 수 있는가 — 서버는 운영지역이 없으면 `NO_SERVICE_AREA` 로 건너뛸 뿐 막지 않는다 */
export function isServiceAreaEmpty(serviceArea) {
  return toServiceAreaPayload(serviceArea) === null
}

/** 목록·상세 한 줄 요약 */
export function serviceAreaSummary(serviceArea) {
  const value = normalizeServiceArea(serviceArea)
  if (value.nationwide) return PLACE_AREA_TEXT.nationwideLabel
  if (value.areas.length === 0) return PLACE_AREA_TEXT.emptyServiceArea

  return `${value.areas.length}개 지역`
}

/**
 * 서버가 기준 주소로 **자동 설정한 운영지역**을 사람에게 말한다 (결정 32 「지역 자동설정」).
 *
 * `null` 이면 **아무 말도 하지 않는다**: 운영지역이 여전히 비어 있다는 뜻이고, 그때 「자동 설정했습니다」
 * 라고 말하면 화면이 일어나지 않은 일을 보고하는 것이 된다. 자동 설정은 항목을 하나 만들므로 첫 항목이
 * 곧 그 시군구다.
 *
 * **이름 뒤에 조사를 붙이지 않는다** — 시군구는 `동해시`처럼 받침이 없는 것과 `양양군`처럼 있는 것이
 * 섞여 있어 은/는·로/으로 판정이 필요해진다. 이름을 콜론 뒤로 빼면 그 판정 자체가 사라진다
 * (`deliveryMenuNotes.js`·`deliveryMenuSync.js` 와 같은 관행).
 */
export function serviceAreaAutoSetMessage(serviceArea) {
  const first = normalizeServiceArea(serviceArea).areas[0]
  if (!first) return null

  return `운영지역을 기준 주소의 시군구로 자동 설정했습니다: ${first.name || first.code}`
}

/** 지역 칩 문구 — 이름과 코드를 함께 보인다(코드가 서버 판정의 1차 축이다) */
export function areaLabel(area) {
  if (!area || !area.code) return ''

  return area.name ? `${area.name} (${area.code})` : String(area.code)
}

/**
 * 특정시 행정구의 **부모 시군구 코드** — 앞 4자리 + `0`. 서버 `PlaceRegionRules.parentSigungu` 와 같은 식이다.
 *
 * 5자리 숫자가 아니면 `null`(판정 불가). 자기 자신이 이미 부모면 같은 값을 돌려준다.
 */
export function parentAreaCode(code) {
  const trimmed = typeof code === 'string' ? code.trim() : ''
  if (trimmed.length !== SIGUNGU_LENGTH || !/^\d+$/.test(trimmed)) return null

  return `${trimmed.slice(0, SIGUNGU_LENGTH - 1)}0`
}

/**
 * 주소 위젯 결과 → 추가할 수 있는 **지역 후보**.
 *
 * 위젯의 `sigungu` 가 `수원시 영통구` 처럼 두 토큰이면 후보가 둘이다 — 「영통구만(41117)」과
 * 「수원시 전체(41110)」. 시 전체 코드는 앞 4자리 + `0`, 이름은 첫 토큰까지다(서버 부모 규칙과 같은 식).
 *
 * 세종처럼 시군구가 없는 곳은 위젯이 주는 `sigunguCode` 를 그대로 쓰고 후보는 하나다.
 *
 * @param {?object} picked `utils/addressPicker` 의 결과(`sigunguCode`·`sido`·`sigungu`)
 * @returns {Array<{code: string, name: string, scope: string, label: string}>}
 */
export function areaCandidatesOf(picked) {
  const code = picked && picked.sigunguCode ? String(picked.sigunguCode).trim() : ''
  if (!code) return []

  const sido = (picked.sido || '').trim()
  const districts = (picked.sigungu || '').trim().split(/\s+/).filter(Boolean)

  const exact = {
    code,
    name: joinName(sido, districts),
    scope: 'DISTRICT',
    label: `${districts[districts.length - 1] || sido || code}만 (${code})`,
  }

  const parent = parentAreaCode(code)
  if (districts.length < 2 || !parent || parent === code) return [exact]

  return [
    exact,
    {
      code: parent,
      name: joinName(sido, districts.slice(0, 1)),
      scope: 'CITY',
      label: `${districts[0]} 전체 (${parent})`,
    },
  ]
}

/**
 * 지역 하나를 더한다 — **새 값을 만들어 돌려준다**(인자를 바꾸지 않는다).
 *
 * 중복·포함 판정이 여기 있는 이유: 같은 코드를 두 번 담으면 칩이 둘인데 뜻은 하나고, 시 전체가 있는데
 * 그 아래 구를 더하면 서버 판정(`parentSigungu`)이 이미 그 구를 품는다 — 사람이 「추가되지 않았다」고
 * 읽지 않도록 그 사실을 말해야 한다.
 *
 * 반대 방향(구가 있는데 시 전체를 더함)은 **더한다**. 그것은 진짜 확장이라 막으면 넓히는 길이 없다.
 * 이미 있는 하위 구는 그대로 두고([ADD_AREA_RESULT.ADDED_COVERING]) 포함된다는 사실만 알린다 —
 * 사람이 지우지 않은 값을 화면이 지우지 않는다.
 *
 * @returns {{serviceArea: object, result: string, message: string}}
 */
export function addArea(serviceArea, area) {
  const current = normalizeServiceArea(serviceArea)
  const code = area && area.code ? String(area.code).trim() : ''
  if (!code) return { serviceArea: current, result: ADD_AREA_RESULT.INVALID, message: PLACE_AREA_TEXT.invalid }

  const existing = current.areas.find((item) => item.code === code)
  if (existing) {
    return { serviceArea: current, result: ADD_AREA_RESULT.DUPLICATE, message: PLACE_AREA_TEXT.duplicate }
  }

  const parent = parentAreaCode(code)
  const cover = parent && parent !== code ? current.areas.find((item) => item.code === parent) : null
  if (cover) {
    return {
      serviceArea: current,
      result: ADD_AREA_RESULT.COVERED,
      // 지역 이름 뒤에 조사를 붙이지 않는다 — 콜론 뒤로 뺀다([serviceAreaAutoSetMessage] 주석)
      message: `이미 추가된 상위 지역에 포함됩니다: ${areaLabel(cover)}`,
    }
  }

  const next = {
    nationwide: current.nationwide,
    areas: current.areas.concat([{ code, name: area.name || '' }]),
  }
  const covered = current.areas.filter((item) => item.code !== code && parentAreaCode(item.code) === code)
  if (covered.length > 0) {
    return {
      serviceArea: next,
      result: ADD_AREA_RESULT.ADDED_COVERING,
      message: `추가했습니다. 이미 있는 하위 지역이 이 안에 포함됩니다: ${covered.map(areaLabel).join(', ')}`,
    }
  }

  return { serviceArea: next, result: ADD_AREA_RESULT.ADDED, message: '' }
}

/** 지역 하나를 뺀다 — 새 값을 만들어 돌려준다 */
export function removeArea(serviceArea, code) {
  const current = normalizeServiceArea(serviceArea)

  return { nationwide: current.nationwide, areas: current.areas.filter((area) => area.code !== code) }
}

/**
 * 「전국」 스위치 — 새 값을 만들어 돌려준다. 지역 목록은 **지우지 않는다**.
 *
 * 「끄면 그대로 돌아온다」는 **저장하기 전 폼 안에서만** 참이다: 저장 본문은 전국이면 지역을 싣지
 * 않고([toServiceAreaPayload]) 서버도 그렇게 저장하므로, 전국으로 한 번 저장한 뒤 끄면 지역은 0곳이다.
 * 그래서 편집기는 지역이 남은 채 전국을 켠 상태를 [nationwideDropsAreasMessage] 로 말한다.
 */
export function setNationwide(serviceArea, on) {
  const current = normalizeServiceArea(serviceArea)

  return { nationwide: on === true, areas: current.areas }
}

/**
 * 전국으로 저장하면 사라질 지역이 있는가 — 있으면 그 사실을 말하고, 없으면 **아무 말도 하지 않는다**.
 *
 * 이름 뒤에 조사를 붙이지 않는 관행은 [serviceAreaAutoSetMessage] 와 같다(건수는 콜론 뒤).
 */
export function nationwideDropsAreasMessage(serviceArea) {
  const value = normalizeServiceArea(serviceArea)
  if (!value.nationwide || value.areas.length === 0) return null

  return `${PLACE_AREA_TEXT.nationwideDropsAreas}: ${value.areas.length}곳`
}

/** 사유 한글 라벨 — 모르는 코드는 그대로 보인다(감추지 않는다) */
export function reasonNm(code) {
  if (!code) return ''

  return AUTO_ENROLL_REASON_NM[code] || code
}

/** 플레이스 단위 건너뜀 안내 — 모르는 코드는 그대로 보인다 */
export function placeSkipNm(code) {
  if (!code) return ''

  return PLACE_SKIP_NM[code] || code
}

/** 지역 판정 근거 표시 */
export function regionByText(regionBy) {
  if (!regionBy) return ''

  return REGION_BY_NM[regionBy] || regionBy
}

/** 거리 표시 — 재지 않았으면(거리 조건 꺼짐·좌표 없음) `-` */
export function distanceText(distanceKm) {
  if (distanceKm === null || distanceKm === undefined || distanceKm === '') return '-'
  const km = Number(distanceKm)
  if (!isFinite(km)) return '-'

  return `${km.toFixed(2)}km`
}

/** 실행 모드·계기 표시 */
export function runModeText(mode) {
  if (!mode) return ''

  return RUN_MODE_NM[mode] || mode
}

export function runTriggerText(triggerTp) {
  if (!triggerTp) return ''

  return RUN_TRIGGER_NM[triggerTp] || triggerTp
}

/**
 * 표 하나가 그리는 행 — **앞에서 자르되 건수는 전부 센다**([PREVIEW_ROW_MAX]).
 *
 * 건수와 목록을 한 함수가 함께 답하는 이유: 둘을 따로 세면 화면이 「50곳」이라고 말하면서 실제로는
 * 수천 곳 중 50곳만 보여 주는, 스스로 거짓말하는 상태가 된다. 사유 묶음과 편입 예정 표가 같은 규칙을 쓴다.
 *
 * @returns {{count: number, items: Array, truncated: number}} `count` = 전체, `items` = 그릴 것
 */
export function cappedRows(rows) {
  const all = Array.isArray(rows) ? rows.filter(Boolean) : []

  return {
    count: all.length,
    items: all.slice(0, PREVIEW_ROW_MAX),
    truncated: all.length > PREVIEW_ROW_MAX ? all.length - PREVIEW_ROW_MAX : 0,
  }
}

/** 잘렸다는 사실 한 줄 — 안 잘렸으면 빈 문자열(할 말이 없다) */
export function rowCapText(view) {
  if (!view || !view.truncated) return ''

  return `${view.count}곳 중 앞 ${view.items.length}곳만 보입니다.`
}

/**
 * 미리보기 목록 → 사유별 묶음. `decision == null` 은 **편입 대상**이라 묶음에 넣지 않는다
 * (사유가 없다는 뜻이지 판정을 안 했다는 뜻이 아니다 — 서버 보고서 §5).
 *
 * 순서는 [AUTO_ENROLL_REASON_NM] 의 키 순서(= 서버 사유 우선순위)이고, 표에 없는 사유는 뒤에 붙인다.
 * 서버가 더 이상 내리지 않는 사유(`OUT_OF_RANGE` — [outOfRangeCountOf] 로 접혔다)가 섞여 와도 묶음이
 * 하나 더 생길 뿐이다: **모르는 사유는 코드를 그대로 보이고, 없는 사유는 묶음을 만들지 않는다.**
 *
 * @returns {Array<{reason: string, label: string, count: number, items: Array, truncated: number}>}
 */
export function reasonGroupsOf(items) {
  const rows = Array.isArray(items) ? items : []
  const grouped = rows.reduce((acc, item) => {
    const reason = item && item.decision
    if (!reason) return acc
    if (!acc[reason]) acc[reason] = []
    acc[reason].push(item)

    return acc
  }, {})

  return orderedReasonCodes(Object.keys(grouped)).map((reason) =>
    Object.assign({ reason, label: reasonNm(reason) }, cappedRows(grouped[reason]))
  )
}

/** 편입 대상 행(`decision == null`) — 미리보기의 첫 표다. 건수는 전체, 목록은 상한까지 */
export function toAddRowsOf(items) {
  return cappedRows((Array.isArray(items) ? items : []).filter((item) => item && !item.decision))
}

/**
 * 「기준 거리 밖 N곳」 — 서버가 `OUT_OF_RANGE` 를 **건수로 접어** 최상위에 내린다(`notInAreaCount` 와 같은 결).
 *
 * 필드가 없으면 0 이다. 거리 조건이 꺼져 있으면 서버가 재지도 않으므로 화면이 그 줄을 그리지 않는다.
 */
export function outOfRangeCountOf(preview) {
  const raw = preview && typeof preview === 'object' ? preview.outOfRangeCount : null

  return Number(raw) || 0
}

/**
 * 「내 범위 밖이라 가려진 매장」 — 서버 `hiddenCounts`(사유 → 건수, 보안 원장 S-B).
 *
 * @returns {Array<{reason: string, label: string, count: number}>}
 */
export function hiddenCountRows(hiddenCounts) {
  if (!hiddenCounts || typeof hiddenCounts !== 'object') return []

  return orderedReasonCodes(Object.keys(hiddenCounts))
    .map((reason) => ({ reason, label: reasonNm(reason), count: Number(hiddenCounts[reason]) || 0 }))
    .filter((row) => row.count > 0)
}

/**
 * 실행 기록 한 줄의 `skippedJson` — 서버가 **문자열**로 내린다(`{"사유": {"count": n, "storeCds": [...]}}`).
 *
 * 읽지 못하면 빈 목록이다 — 기록 한 줄이 깨졌다고 표 전체를 못 그리게 만들지 않는다.
 *
 * @returns {Array<{reason: string, label: string, count: number}>}
 */
export function skippedRowsOf(skippedJson) {
  const parsed = parseJsonObject(skippedJson)
  if (!parsed) return []

  return orderedReasonCodes(Object.keys(parsed))
    .map((reason) => ({
      reason,
      label: reasonNm(reason),
      count: Number(parsed[reason] && parsed[reason].count) || 0,
    }))
    .filter((row) => row.count > 0)
}

/** 기록 한 줄의 건너뜀 요약 문자열 — 없으면 빈 문자열 */
export function skippedSummaryText(skippedJson) {
  return skippedRowsOf(skippedJson)
    .map((row) => `${row.label} ${row.count}`)
    .join(' · ')
}

/**
 * 플레이스 상세 응답 → 폼의 **새 세대 필드**.
 *
 * 거리 상한의 이름은 어드민 API 전체에서 `maxDeliveryKm` **하나**다 — 단건 읽기·미리보기·쓰기가 같은
 * 이름을 쓰기로 서버와 확정했고 아직 어디에도 배포되지 않았으므로, 호환 분기를 남기지 않는다.
 * 분기를 남기면 어느 이름이 사실인지 코드가 영원히 답하지 못한다.
 *
 * `callpopOwned` 는 **읽기 전용 사실**이다 — 폼에 실어 두는 이유는 화면의 세 자리(수정 폼·상세·목록 행)가
 * 같은 술어([isCallpopOwned])로 묻기 때문이고, 저장 본문에는 나가지 않는다([PLACE_OWNED_KEYS]).
 */
export function placeFieldsFromServer(detail) {
  const row = detail && typeof detail === 'object' ? detail : {}

  return {
    callpopOwned: row.callpopOwned === true,
    autoEnrollYn: row.autoEnrollYn === 'Y' ? 'Y' : 'N',
    serviceArea: normalizeServiceArea(row.serviceArea),
    maxDeliveryKm: toNumberOrNull(row.maxDeliveryKm),
    partnerTpCd: isOwnerPartnerEmpty(row.partnerTpCd) ? null : row.partnerTpCd,
    baseAddr: row.baseAddr || '',
    baseAddrDtl: row.baseAddrDtl || '',
    baseZipCd: row.baseZipCd || '',
    baseLat: row.baseLat === null || row.baseLat === undefined ? null : row.baseLat,
    baseLng: row.baseLng === null || row.baseLng === undefined ? null : row.baseLng,
    baseBjdCd: row.baseBjdCd || '',
  }
}

/**
 * 폼 → 저장 본문의 **새 세대 필드**. 서버는 「본문에 키가 있는 새 필드만」 반영하므로(부재 = 불변),
 * 화면은 **자기가 보이는 필드만** 싣고 보이는 것은 언제나 싣는다 — 빈 값은 `null`(지움)이라는 뜻이다.
 *
 * 두 축이 갈린다. 둘 다 같은 규칙의 다른 얼굴이다 — **보이는 것이 곧 보내는 것**:
 *
 * 1. `partnerTpCd` — 소유 파트너는 **오더엔 내부 관리자만** 바꿀 수 있고(서버 권한 표 ⑥), 그 밖의
 *    역할에게 화면은 읽기 전용 텍스트만 보인다.
 * 2. [PLACE_CALLPOP_FIELD_KEYS] — 자동 편입·운영지역·기준 주소는 **콜팝 소유 플레이스에서만** 보인다
 *    (M3 리얼 선결 ⑩). 안 보이는 칸의 값을 되보내면 사람이 본 적 없는 저장이 되고, 특히 옛 판에서
 *    값이 들어 있는 비콜팝 플레이스에서는 **자기가 켠 적 없는 설정이 조용히 지워진다**.
 *
 * 어느 축이든 다루지 않는 값을 되보내면 같은 값일 때만 통과하는 위태로운 저장이 되므로,
 * **키를 아예 넣지 않는다**(부재 = 불변이 그 답이다).
 *
 * 기준 주소 여섯은 서버가 한 덩어리로 다시 쓴다(빈 주소 = 여섯을 함께 지움).
 *
 * @param {?object} form 입력 폼
 * @param {?{ownerPartner: boolean, callpopFields: boolean}} options `ownerPartner` 가 참일 때만
 *   `partnerTpCd` 를 싣고, `callpopFields` 가 **거짓일 때만** 콜팝 축 필드를 뺀다(기본은 싣는다)
 */
export function placeFieldsToPayload(form, options) {
  const row = form && typeof form === 'object' ? form : {}
  const baseAddr = (row.baseAddr || '').trim()

  const callpopFields = {
    autoEnrollYn: row.autoEnrollYn === 'Y' ? 'Y' : 'N',
    serviceArea: toServiceAreaPayload(row.serviceArea),
    maxDeliveryKm: toIntOrNull(row.maxDeliveryKm),
    baseAddr: baseAddr || null,
    baseAddrDtl: baseAddr ? (row.baseAddrDtl || '').trim() || null : null,
    baseZipCd: baseAddr ? row.baseZipCd || null : null,
    baseLat: baseAddr ? toNumberOrNull(row.baseLat) : null,
    baseLng: baseAddr ? toNumberOrNull(row.baseLng) : null,
    baseBjdCd: baseAddr ? row.baseBjdCd || null : null,
  }
  const payload = options && options.callpopFields === false ? {} : callpopFields
  if (!options || options.ownerPartner !== true) return payload

  return Object.assign({}, payload, {
    partnerTpCd: isOwnerPartnerEmpty(row.partnerTpCd) ? null : row.partnerTpCd,
  })
}

/**
 * 소유 파트너가 **비었는가** — 한 질문에 한 답이다.
 *
 * 값이 없다는 것을 세 가지 모양으로 만난다: 서버가 안 내린 `undefined`, 지워진 `null`, 그리고
 * `el-select` 의 `clearable` 이 올리는 **빈 문자열**. 어느 한 자리만 셋을 다 알면(저장 본문이 그랬다)
 * 화면은 「없음」이라고 안 말하는데 저장은 「없음」으로 나가는, 보이는 것과 보내는 것이 갈린 상태가 된다.
 * 옵션 계산·표시 문구·페이로드가 전부 이 술어를 읽는다.
 */
export function isOwnerPartnerEmpty(partnerTpCd) {
  return partnerTpCd === null || partnerTpCd === undefined || partnerTpCd === ''
}

/**
 * **이 플레이스에 콜팝 규칙이 걸리는가** — 서버가 답한 사실을 읽는다(`GET /place` 항목·`GET /place/{id}`).
 *
 * 화면은 이 질문을 스스로 판정하지 않는다. `partnerTpCd` 를 콜팝 코드와 비교하려면 **환경마다 다른 cd** 를
 * 화면이 알아야 하고 그 순간 판정 사본이 생긴다 — 그래서 서버가 같은 소유 축 술어
 * (`PlacePartnerRules.ownedBy`)로 불리언을 내린다(서버 보고서 §2-0). 필드 이름을 아는 자리도 이 함수 하나다.
 *
 * 필드가 없는 응답(서버가 아직 안 나간 환경)은 **거짓**이다: 콜팝 어휘와 콜팝 전용 UI 를 내보이지 않는
 * 쪽으로 틀린다. 그래서 이 회차의 배포 순서는 **appserver 먼저 → admin-web 나중**이다.
 *
 * @param {?object} row 플레이스 목록 항목·상세 응답·그 값으로 채운 폼
 */
export function isCallpopOwned(row) {
  return !!(row && row.callpopOwned === true)
}

/**
 * 이 호출자가 플레이스의 **조건해제(특별) 지정**을 바꿀 수 있는가 — 서버가 답한다(R-H 2차 리뷰 · 서버 H2).
 *
 * 플레이스 목록(`GET /place`)·상세(`GET /place/{id}`) 응답 최상위의 `capabilities.canDesignateConditionExempt` 는 저장
 * 판정(`PlaceService.resolveConditionExempt` → `CallerScope.canDesignateConditionExempt` — 내부 `admin`·`mng` 와 범위에
 * 묶이지 않은 `callpopAdmin` 만 참)과 **같은 술어**다. 종전에는 화면이 역할 이름으로 추측했고, 범위에 묶인 콜팝
 * 관리자처럼 둘이 갈리는 계정에서 스위치가 열려 저장이 403(이 앱에서는 로그아웃)이었다.
 *
 * 칸이 없는 옛 응답·모양이 다른 값은 **거짓**이다 — 잠그는 쪽이 안전하다(바꿀 수 있는 사람이 잠깐 못 바꿀 뿐이다).
 * 등록 폼은 상세가 없으므로 목록 응답의 값을 폼에 담아 같은 자리로 묻는다. 이 값은 읽기 전용 사실이라 저장 본문에
 * 나가지 않는다([PLACE_OWNED_KEYS]).
 *
 * @param {?object} source `capabilities` 를 가진 목록·상세 응답 또는 그 값으로 채운 폼
 * @returns {boolean}
 */
export function canDesignateConditionExemptOf(source) {
  const capabilities = source && source.capabilities

  return !!(capabilities && capabilities.canDesignateConditionExempt === true)
}

/**
 * 편성 후보 목록의 「우리동네 입점 매장만」 **기본값** — 편성 조회(`GET /place/{id}/stores`)의 `entryConditioned`.
 *
 * 입점 필터는 편성 판정(재정리 §3-1)이 걸리는 플레이스에서만 기본으로 켠다: 거기서는 미입점 매장이 일반
 * 1곳에만 들어가므로 입점 매장이 기본 후보다. 판정이 안 걸리는 플레이스(조건 해제이거나 **콜팝 소유가 아닌**
 * 오더톡 행사 플레이스 등)는 미입점 매장도 그대로 편성 대상이라 끈 채로 연다.
 *
 * 「걸리는가」는 [isCallpopOwned] 와 같은 이유로 화면이 판정하지 않는다 — 서버가 같은 술어
 * (`PlaceEntryPolicy.conditioned`)로 불리언을 내린다. 필드가 없는 응답(서버가 아직 안 나간 환경)은 **거짓**이다:
 * 후보를 덜 좁히는 쪽으로 틀리고, 추가가능 판정은 어차피 서버가 행마다 답한다.
 *
 * @param {?object} storesResponse `GET /place/{id}/stores` 응답
 */
export function subscribedOnlyDefault(storesResponse) {
  return !!(storesResponse && storesResponse.entryConditioned === true)
}

/**
 * 후보 매장의 **우리동네TV 입점 표시** — 콜팝 소유 플레이스에서만 말한다.
 *
 * 입점 여부는 콜팝 세계의 축이다(편성 판정 §3-1 의 입력). 오더톡 행사 플레이스에서는 아무 판정에도
 * 쓰이지 않으므로 「우리동네 미조회」 같은 문구는 운영자에게 뜻 없는 콜팝 어휘다 — 빈 문자열로 답해
 * 후보 행에 매장 ID 만 남긴다. 「콜팝 소유인가」는 서버가 답한 사실([isCallpopOwned])을 넘겨받는다.
 *
 * @param {?{subscription: string}} store 후보 목록 항목(`GET /place/{id}/stores/available`)
 * @param {boolean} callpopOwned 이 플레이스가 콜팝 소유인가
 */
export function subscriptionText(store, callpopOwned) {
  if (callpopOwned !== true || !store) return ''
  if (store.subscription === 'SUBSCRIBED') return '우리동네 입점'
  if (store.subscription === 'NOT_SUBSCRIBED') return '우리동네 미입점'
  if (store.subscription === 'UNCHECKED') return '우리동네 미조회'

  return ''
}

/**
 * 편성 후보 한 행의 판정 — 서버 `PlaceEntryPolicy.Entry` 와 같은 어휘(`GET /place/{id}/stores/available` 의 `entry`).
 * 막힌 판정은 서버의 판정 순서대로 적었다(`INELIGIBLE` → `OUT_OF_RANGE` → `BLOCKED_IN_OTHER_GENERAL`).
 */
export const PLACE_ENTRY = Object.freeze({
  ADDABLE: 'ADDABLE',
  /** 콜팝 참여 조건 미달 — 사유는 같은 항목의 `ineligibleReasons`(`NO_MID`·`NO_MENU`, 순서 고정)다(R-N) */
  INELIGIBLE: 'INELIGIBLE',
  /**
   * 거리 밖 — 플레이스에 기준 좌표와 최대 배달거리가 둘 다 있고(거리 축), 「거리 안」이 확인되지 않았다(R-X).
   * 거리를 쟀으면 같은 항목의 `distanceKm` 에 실리고, 매장 좌표가 없으면 `null` 이다.
   */
  OUT_OF_RANGE: 'OUT_OF_RANGE',
  BLOCKED_IN_OTHER_GENERAL: 'BLOCKED_IN_OTHER_GENERAL',
})

/**
 * 편성 후보 한 행의 **판정 표시** — `{type, text, reason, blocked}`. 뱃지·사유 줄·「추가」 잠금이 모두 이 한 답을 읽는다.
 *
 * 판정은 서버(`PlaceEntryPolicy.entry`)가 내리고 화면은 그 값만 그린다. 판정 순서도 서버의 것이다 — 참여 조건
 * 미달·거리 밖이면 `blockedBy` 가 채워져 와도(서버는 센 값을 지우지 않는다) `entry` 가 먼저 가른다.
 *
 * - 막힌 판정은 모두 같은 뱃지·같은 자리(사유 줄)·같은 잠금이다. 종전에는 뱃지와 「추가」 잠금이 `entry` 를
 *   따로 비교해, 막힌 판정이 하나 늘면 두 자리를 함께 고쳐야 했다 — 이제 잠금은 이 답의 `blocked` 다.
 * - 참여 조건 미달의 사유 줄은 매장 입점 현황이 참여 등록을 잠글 때 쓰는 문장([joinLockReason])을 그대로
 *   쓴다 — 같은 판정(`CallpopJoinEligibility`)을 두 화면이 다른 낱말로 말하지 않게.
 * - 거리 밖의 사유 줄은 판정만 말한다. 거리(km)는 거리 축이 켜진 플레이스의 **모든 후보 행**이 같은 자리에서
 *   보이므로([candidateDistanceText]) 여기서 한 번 더 적지 않는다. 거리를 재지 못한 거리 밖(매장 좌표 없음 —
 *   축이 켜졌는데 `distanceKm` 가 없다)은 「매장 좌표 없음」이다 — 뱃지·잠금은 같고 사람이 고칠 곳이 다르다.
 * - 판정이 없는 행(뺐다가 되돌린 등록 매장)은 중립이다 — 서버가 말하지 않은 「추가가능」을 지어내지 않고
 *   「추가」도 열어 둔다(이미 등록된 매장이라 저장 때 판정 대상이 아니다). 모르는 새 값도 이 갈래로 떨어진다.
 *
 * @param {?{entry: string, blockedBy: string[], ineligibleReasons: string[]}} store 후보 목록 항목
 * @returns {{type: string, text: string, reason: string, blocked: boolean}} `type` 은 `el-tag` 의 type
 */
export function candidateEntryView(store) {
  const row = store && typeof store === 'object' ? store : {}
  if (row.entry === PLACE_ENTRY.ADDABLE) {
    return { type: 'success', text: PLACE_AREA_TEXT.entryAddable, reason: '', blocked: false }
  }
  if (row.entry === PLACE_ENTRY.INELIGIBLE) {
    const reason = joinLockReason({ eligible: false, reasons: row.ineligibleReasons })

    return { type: 'danger', text: PLACE_AREA_TEXT.entryBlocked, reason, blocked: true }
  }
  if (row.entry === PLACE_ENTRY.OUT_OF_RANGE) {
    return {
      type: 'danger',
      text: PLACE_AREA_TEXT.entryBlocked,
      reason: measuredKmOf(row) === null ? PLACE_AREA_TEXT.entryNoGps : PLACE_AREA_TEXT.entryOutOfRange,
      blocked: true,
    }
  }
  if (row.entry === PLACE_ENTRY.BLOCKED_IN_OTHER_GENERAL) {
    const names = Array.isArray(row.blockedBy) ? row.blockedBy.join(', ') : ''

    return {
      type: 'danger',
      text: PLACE_AREA_TEXT.entryBlocked,
      reason: `이미 일반 플레이스 ${names} 에 가입됨`,
      blocked: true,
    }
  }

  return { type: 'info', text: PLACE_AREA_TEXT.entryRestored, reason: '', blocked: false }
}

/**
 * 편성 후보 한 행의 **거리** — 「기준 주소에서 1.23km」. 재지 않았으면 빈 문자열이다(할 말이 없다).
 *
 * 서버는 거리 축(기준 좌표와 최대 배달거리가 둘 다 있다)이 켜진 플레이스에서만 `distanceKm` 를 숫자로 싣고,
 * 축이 꺼졌거나 매장 좌표가 없으면 `null` 이다. 그래서 「이 플레이스에 거리 축이 켜졌는가」를 화면이 따로
 * 묻지 않는다 — 숫자가 온 행이 곧 잰 행이다. 판정(`entry`)과 무관하게 같은 자리에 보인다: 거리 밖인 행의
 * 사유 줄([candidateEntryView])은 판정만 말하고 거리는 여기가 말한다. 반올림은 서버가 한다(소수 둘째 자리).
 *
 * @param {?{distanceKm: ?number}} store 후보 목록 항목(`GET /place/{id}/stores/available`)
 */
export function candidateDistanceText(store) {
  const km = measuredKmOf(store)
  if (km === null) return ''

  return `${PLACE_AREA_TEXT.candidateDistancePrefix} ${distanceText(km)}`
}

/** 후보 행의 잰 거리 — 숫자가 아니면 `null`(재지 않았다). 거리 표시와 거리 밖 사유가 같은 답을 읽는다 */
function measuredKmOf(store) {
  return store && typeof store === 'object' ? toNumberOrNull(store.distanceKm) : null
}

/**
 * 이 파트너를 소유로 고르면 콜팝 규칙이 걸리는가 — `GET /place/partners` 항목의 `callpopRules`.
 *
 * [isCallpopOwned] 와 **같은 서버 술어**의 다른 자리다(서버 보고서 §2-0-bis). 등록 폼은 아직 저장하지
 * 않은 선택값을 들고 있어 플레이스 읽기 모델을 볼 수 없으므로, 선택 상자 항목이 그 답을 같이 준다.
 */
export function partnerAppliesCallpopRules(partner) {
  return !!(partner && partner.callpopRules === true)
}

/**
 * 등록 폼이 **만들** 플레이스에 콜팝 규칙이 걸리는가 — 서버 생성 규칙과 같은 말이다
 * (`PlacePartnerRules.defaultOwnerOf` + `PlaceService.resolveOwnerPartner`, 감사 §5 Q1 (a)).
 *
 * - 소유를 고를 수 있는 역할(오더엔 내부) → **고른 항목**이 답한다([partnerAppliesCallpopRules]).
 *   비우면 소유 없음이므로 거짓이다 — 서버의 옛 「비우면 콜팝」 폴백은 이 회차에 없어졌다.
 * - 고를 수 없는 역할 → 서버의 기본 소유가 답한다: 콜팝 사업 역할이면 콜팝, 그 밖(사업장 관리자)은 없음.
 *
 * @param {?{callpopCaller: boolean, ownerPartnerEditable: boolean, partner: ?object}} context
 *   `callpopCaller` = `adminRole.isCallpopRole` · `ownerPartnerEditable` = `adminRole.canManagePlaceOwnerPartner`
 */
export function newPlaceCallpopOwned(context) {
  const row = context && typeof context === 'object' ? context : {}
  if (row.ownerPartnerEditable === true) return partnerAppliesCallpopRules(row.partner)

  return row.callpopCaller === true
}

/**
 * **이 저장 뒤에도** 콜팝 규칙이 걸리는가 — 저장 본문에 콜팝 축을 실을지 정한다(수정 모드).
 *
 * 화면의 **표시**는 서버 사실([isCallpopOwned])이 정한다: 지금 그 플레이스에 걸려 있는 규칙이 그것이고,
 * 소유 파트너 목록은 못 읽어도 화면이 도는 부가 조회라 그 실패로 칸이 사라지면 안 된다. 그런데 **본문**은
 * 같은 요청이 만들 상태를 따라야 한다 — 오더엔 내부 관리자가 콜팝 플레이스의 소유를 비우거나 다른
 * 파트너로 옮기면서 저장하면, 저장 뒤 그 플레이스에는 콜팝 규칙이 없다. 그때 자동편입·운영지역·기준
 * 주소를 같은 본문에 실으면 **아무도 다시 볼 수 없는 설정**이 그 플레이스에 남고(두 칸 다 소유 축으로
 * 가려진다), 누군가 소유를 콜팝으로 되돌리는 순간 검토된 적 없는 값으로 자동 편입이 되살아난다.
 *
 * 소유를 고칠 수 없는 역할(본문에 `partnerTpCd` 가 없다)과 소유를 바꾸지 않는 저장은 서버 사실과 같은
 * 답이다 — 그 저장은 소유를 옮기지 않으므로 물을 것이 없다.
 *
 * @param {?object} saved 서버에서 읽어 온 값([placeFieldsFromServer] 의 답)
 * @param {?object} form 지금 폼
 * @param {?{ownerPartnerEditable: boolean, partner: ?object}} context 선택 상자를 다루는 역할인가 · 고른 항목
 */
export function nextCallpopOwned(saved, form, context) {
  const fact = isCallpopOwned(saved)
  const row = context && typeof context === 'object' ? context : {}
  if (row.ownerPartnerEditable !== true) return fact

  const before = saved && typeof saved === 'object' ? saved.partnerTpCd : null
  const after = form && typeof form === 'object' ? form.partnerTpCd : null
  if (sameOwnerPartner(before, after)) return fact

  return partnerAppliesCallpopRules(row.partner)
}

/**
 * **콜팝 축의 두 답을 함께 낸다** — 칸이 화면에 보이는가(`shown`)와 저장 본문에 실리는가(`inBody`).
 *
 * 규칙은 **하나**다:
 *
 * ```
 * inBody = shown ∧ nextOwned
 * ```
 *
 * 두 조건이 함께 필요한 이유가 서로 반대쪽이다:
 *
 * - `shown` 없이 실으면 **사람이 본 적 없는 값**이 저장된다. 수정 폼의 `shown` 은 서버 사실이므로
 *   (지금 걸려 있는 규칙이 그것이고, 소유 파트너 목록은 못 읽어도 화면이 도는 부가 조회다) 소유를
 *   콜팝으로 **옮기는** 저장에서는 칸이 숨겨진 채다 — 그때 폼에 남아 있던 옛 값이 정규화되어 나가고,
 *   기준 주소가 실리면 서버가 운영지역까지 자동 설정한다(결정 32). 아무도 그것을 본 적이 없다.
 * - `nextOwned` 없이 실으면 **다시 볼 수 없는 값**이 남는다([nextCallpopOwned] 주석).
 *
 * 그래서 두 답을 한 함수가 함께 낸다. 뷰의 `v-if` 들과 `save()` 는 이 한 답의 두 자리를 읽고,
 * 부품([newPlaceCallpopOwned]·[nextCallpopOwned])을 각자 다시 조립하지 않는다 — 조립을 두 곳에서
 * 하면 「어느 소유가 정하는가」가 갈려, 방금 그 갈림이 이 함수를 만든 결함이었다(R2).
 *
 * 등록 폼은 `shown === nextOwned` 다(만들어질 소유가 곧 저장 뒤 소유다) — 종전 동작과 같다.
 *
 * @param {?object} context `{isNew, saved, form, callpopCaller, ownerPartnerEditable, partner}`
 *   `saved` = [placeFieldsFromServer] 의 답 · `form` = 지금 폼 · 나머지는 [newPlaceCallpopOwned] 와 같다
 * @returns {{shown: boolean, inBody: boolean}}
 */
export function callpopFieldAxis(context) {
  const row = context && typeof context === 'object' ? context : {}
  const isNew = row.isNew === true
  const shown = isNew ? newPlaceCallpopOwned(row) : isCallpopOwned(row.saved)
  const nextOwned = isNew ? shown : nextCallpopOwned(row.saved, row.form, row)

  return { shown, inBody: shown && nextOwned }
}

/**
 * 소유 파트너 표시 — 이름이 없으면 코드, 값이 없으면 **등록인지 수정인지에 따라 다른 말**.
 *
 * 빈 값의 뜻이 갈리기 때문이다: 수정의 빈 값은 진짜 「없음」(편성 통지가 나가지 않는다)이고, 등록의 빈
 * 값은 **만들어질 소유**가 정한다 — 콜팝 역할이 만들면 서버가 콜팝으로 각인하고([newPlaceCallpopOwned]),
 * 그 밖은 소유 없이 등록된다. 등록 화면이 어느 쪽이든 「없음」이라고만 말하면 화면이 일어날 일을
 * 반대로 약속한다.
 *
 * 등록의 「소유 없음」 갈래는 **목소리**로 한 번 더 갈린다: 선택 상자를 다루는 오더엔 내부 관리자에게는
 * 「비우면 …」이 그 사람의 행동이지만, 상자를 보지 못하는 역할에게는 비울 것이 없다(감사 R1 low #6).
 *
 * @param {?object} row `partnerTpCd`·`partnerTpNm` 을 가진 폼·상세 응답
 * @param {?{isNew: boolean, callpopOwned: boolean, ownerPartnerEditable: boolean}} options
 *   등록 모드인가 · 만들어질 소유가 콜팝인가 · 소유를 고를 수 있는 역할인가
 */
export function ownerPartnerText(row, options) {
  const source = row && typeof row === 'object' ? row : {}
  const mode = options && typeof options === 'object' ? options : {}
  if (isOwnerPartnerEmpty(source.partnerTpCd)) {
    if (mode.isNew !== true) return PLACE_AREA_TEXT.ownerPartnerNone
    if (mode.callpopOwned === true) return PLACE_AREA_TEXT.ownerPartnerCallpopNew

    return mode.ownerPartnerEditable === true
      ? PLACE_AREA_TEXT.ownerPartnerNoneNew
      : PLACE_AREA_TEXT.ownerPartnerNoneNewReadOnly
  }

  return source.partnerTpNm || String(source.partnerTpCd)
}

/**
 * 후보 0건의 문구 — **빈 흰 칸은 장애로 읽힌다**(감사 §4-7. 종전에는 오른쪽 패널이 그냥 비었다).
 *
 * 말하는 것은 **확인된 사실 하나**([PLACE_AREA_TEXT.availableEmpty])이고, 원인은 지목하지 않는다.
 * 대신 지금 실제로 후보를 좁히고 있는 축만 「확인할 것」으로 든다:
 *
 * - 검색어·지역이 걸려 있으면 그것(이 목록을 만든 조건이므로 첫 번째다)
 * - 「우리동네 입점 매장만」이 켜져 있으면 그 토글 — 콜팝 축이 아니라 우리동네TV 축이라 어느
 *   플레이스에서도 후보를 줄인다
 * - 콜팝 소유 플레이스면 참여 원장(후보 = 콜팝 참여 ACTIVE ∩ 내 가시 범위, 서버 보고서 §2-1 ①)
 *
 * 걸린 축이 없으면 사실 한 줄로 끝난다 — 확인할 곳이 없다는 것도 답이다.
 *
 * @param {?{callpopOwned: boolean, subscribedOnly: boolean, searchQ: string, region: string}} context
 *   `searchQ`·`region` 은 **지금 목록을 만든 조건**이어야 한다(입력칸의 나중 값이 아니다).
 */
export function availableStoresEmptyText(context) {
  const row = context && typeof context === 'object' ? context : {}
  const checks = [
    storeFilterApplied(row) ? PLACE_AREA_TEXT.availableEmptyCheckFilter : '',
    row.subscribedOnly === true ? PLACE_AREA_TEXT.availableEmptyCheckSubscribed : '',
    row.callpopOwned === true ? PLACE_AREA_TEXT.availableEmptyCheckCallpop : '',
  ].filter(Boolean)
  if (checks.length === 0) return PLACE_AREA_TEXT.availableEmpty

  return `${PLACE_AREA_TEXT.availableEmpty} ${PLACE_AREA_TEXT.availableEmptyCheckPrefix}: ${checks.join(' · ')}`
}

/** 이 목록에 검색 조건이 걸려 있는가 — 빈 문자열·공백만은 조건이 아니다 */
export function storeFilterApplied(context) {
  const row = context && typeof context === 'object' ? context : {}

  return [row.searchQ, row.region].some((value) => typeof value === 'string' && value.trim() !== '')
}

/**
 * 편성 저장이 막혔다 — **서버 문장 + 그 문장이 가리키는 매장의 이름**(감사 ⑨).
 *
 * 사유 코드로 가르지 않는다 — 서버 문장(`PlaceEntryBlockedMessages`)이 사유의 정본이다. 참여 조건 미달
 * (`INELIGIBLE`, R-N)·거리 밖(`OUT_OF_RANGE`, R-X)도 서버 문장 그대로 통과시킨다.
 *
 * 서버는 호출자가 부른 축으로 매장을 가리킨다(`PlaceEntryBlocked.label`). 화면은 `storeCd` 목록을 보내므로
 * `NOT_PARTICIPATING` 문장은 **번호만** 말하고, 사람은 그 번호로 어느 매장인지 모른다. 그래서 화면이 이미
 * 들고 있는 행에서 「문장이 이 번호를 가리키는가」를 물어 이름을 덧붙인다 — **문장을 파싱하지 않는다**.
 * 서버가 문구를 고쳐도 답이 깨지지 않고, 못 찾으면 서버 문장만 보인다(화면이 이름을 지어내지 않는다).
 *
 * 한계: 문장의 다른 자리에 어떤 매장의 번호와 같은 숫자가 들어 있으면 그 행도 함께 이름이 붙는다
 * (`BLOCKED_IN_OTHER_GENERAL` 은 이름으로 가리키므로 보통 아무 행도 안 붙는다). 번호가 문장에 없는 행을
 * 붙이는 반대 방향의 틀림은 없다.
 *
 * @param {?string} message 서버 문장(`HttpError.message`)
 * @param {?Array} rows 지금 편성 목록(`[{storeCd, storeNm, storeId}]`)
 */
export function entryBlockedText(message, rows) {
  const sentence = typeof message === 'string' ? message.trim() : ''
  if (!sentence) return ''

  const named = (Array.isArray(rows) ? rows : [])
    .filter((row) => row && mentionsStoreCd(sentence, row.storeCd))
    .map((row) => `${row.storeNm || row.storeId || row.storeCd}(${row.storeCd})`)
  if (named.length === 0) return sentence

  return `${sentence} — 해당 매장: ${named.join(', ')}`
}

/**
 * 이 저장이 소유 파트너를 **옮기는가** — 있던 소유가 다른 파트너 또는 「없음」이 되는 저장이다.
 *
 * 없던 소유를 처음 지정하는 것은 옮김이 아니다(끊길 운영자 연결이 없다). 비교의 기준점은 폼의 옛 상태가
 * 아니라 **서버가 준 값**이다(`placeFieldsFromServer` 결과).
 *
 * @param {?object} saved 서버에서 읽어 온 값
 * @param {?object} form 지금 폼
 */
export function ownerPartnerTransfers(saved, form) {
  const before = saved && typeof saved === 'object' ? saved.partnerTpCd : null
  if (isOwnerPartnerEmpty(before)) return false
  const after = form && typeof form === 'object' ? form.partnerTpCd : null

  return !sameOwnerPartner(before, after)
}

/** 서버가 받아 주는 최대 배달거리인가 — 서버 `PlaceServiceAreaRules.validDistanceKm` 와 같은 식(없음도 참) */
export function validMaxDeliveryKm(value) {
  const km = toIntOrNull(value)

  return km === null || (km >= MAX_DELIVERY_KM_MIN && km <= MAX_DELIVERY_KM_MAX)
}

/**
 * **콜팝 소유 플레이스에서만 화면에 있는 필드** — 자동 편입·운영지역·기준 주소 여섯·최대 거리.
 *
 * 세 UI 의 노출과 저장 본문이 **같은 목록**을 읽는다(M3 리얼 선결 ⑩): 안 보이는 칸은 안 나간다.
 * 자동 편입 판정의 입력([AUTO_ENROLL_INPUT_KEYS])은 이 목록에서 스위치 자신만 뺀 것이라 파생으로 둔다 —
 * 두 목록을 따로 적으면 필드가 하나 늘 때 한쪽만 늘어난다.
 */
export const PLACE_CALLPOP_FIELD_KEYS = Object.freeze([
  'autoEnrollYn',
  'serviceArea',
  'maxDeliveryKm',
  'baseAddr',
  'baseAddrDtl',
  'baseZipCd',
  'baseLat',
  'baseLng',
  'baseBjdCd',
])

/**
 * 이 모듈이 **본문에 싣는 것을 정하는** 키들 — 폼에 같은 이름이 있어도 그 값이 그대로 나가지 않는다.
 *
 * 폼은 통째로 본문이 되므로(종전 동작), `partnerTpCd` 를 키 목록에서 빼는 것만으로는 부족하다.
 * 폼의 그 키가 스프레드로 따라나가기 때문이다. [placeSavePayload] 가 먼저 걷어내고 나서 [placeFieldsToPayload]
 * 의 답을 얹는 이유다. `partnerTpNm`·`callpopOwned`·`capabilities`([canDesignateConditionExemptOf]) 는 서버가 읽기
 * 전용으로 내리는 사실이라 **언제나** 빠진다.
 */
export const PLACE_OWNED_KEYS = Object.freeze(
  PLACE_CALLPOP_FIELD_KEYS.concat(['partnerTpCd', 'partnerTpNm', 'callpopOwned', 'capabilities'])
)

/**
 * 폼 → 저장 본문 **전체**. 종전 필드는 지금처럼 전부 나가고, 새 세대 필드는 [placeFieldsToPayload] 가
 * 정한 것만 실린다 — 「무엇이 나가는가」를 묻는 자리가 하나다.
 */
export function placeSavePayload(form, options) {
  const row = form && typeof form === 'object' ? form : {}

  return Object.assign(without(row, PLACE_OWNED_KEYS), placeFieldsToPayload(row, options))
}

/** 기준 주소 편집기가 다루는 값만 뽑는다 — 폼 전체를 컴포넌트에 넘기지 않는다 */
export function baseAddressOf(form) {
  const row = form && typeof form === 'object' ? form : {}

  return {
    baseAddr: row.baseAddr || '',
    baseAddrDtl: row.baseAddrDtl || '',
    baseZipCd: row.baseZipCd || '',
    baseLat: row.baseLat === null || row.baseLat === undefined ? null : row.baseLat,
    baseLng: row.baseLng === null || row.baseLng === undefined ? null : row.baseLng,
    baseBjdCd: row.baseBjdCd || '',
    maxDeliveryKm: row.maxDeliveryKm === null || row.maxDeliveryKm === undefined ? null : row.maxDeliveryKm,
  }
}

/** 빈 기준 주소 — 「지우기」는 여섯을 **함께** 비운다(서버 규칙과 같다). 거리 상한은 별개 축이라 남긴다 */
export function clearBaseAddress(value) {
  const row = value && typeof value === 'object' ? value : {}

  return {
    baseAddr: '',
    baseAddrDtl: '',
    baseZipCd: '',
    baseLat: null,
    baseLng: null,
    baseBjdCd: '',
    maxDeliveryKm: row.maxDeliveryKm === null || row.maxDeliveryKm === undefined ? null : row.maxDeliveryKm,
  }
}

/** 주소 위젯 결과 → 기준 주소 다섯 필드(상세주소는 사람이 입력한다) */
export function baseAddressFromPicked(value, picked) {
  const current = baseAddressOf(value)
  if (!picked || !picked.address) return current

  return Object.assign({}, current, {
    baseAddr: picked.address,
    baseAddrDtl: '',
    baseZipCd: picked.zonecode || '',
    baseLat: toNumberOrNull(picked.lat),
    baseLng: toNumberOrNull(picked.lng),
    baseBjdCd: picked.bcode || '',
  })
}

/**
 * 상세·요약의 기준 주소 한 줄 — 없으면 [PLACE_AREA_TEXT.baseAddressEmpty].
 *
 * 상세주소가 비면 **자리도 없다**: 빈 칸을 그대로 이어 붙이면 이중 공백이 남는다. 문자열을 잇는 규칙이
 * 화면마다 적히지 않도록 여기서 만든다.
 */
export function baseAddressText(value) {
  const base = baseAddressOf(value)
  if (!base.baseAddr) return PLACE_AREA_TEXT.baseAddressEmpty
  const km = base.maxDeliveryKm === null ? '거리 조건 없음' : `최대 ${base.maxDeliveryKm}km`

  return `${[base.baseAddr, base.baseAddrDtl].filter(Boolean).join(' ')} · ${km}`
}

/** 기준 주소와 거리 상한이 **둘 다** 있어야 거리 조건이 켜진다(서버 `distanceFilterOn` 과 같은 식) */
export function distanceFilterOn(value) {
  const row = baseAddressOf(value)

  return (
    toNumberOrNull(row.baseLat) !== null &&
    toNumberOrNull(row.baseLng) !== null &&
    toIntOrNull(row.maxDeliveryKm) !== null
  )
}

/**
 * 자동 편입 판정이 읽는 **입력** — 운영지역·기준 주소 여섯·최대 거리. `autoEnrollYn` 자신은 아니다.
 *
 * 서버가 저장된 값으로 판정하므로, 비교는 **저장 본문으로 정규화한 뒤**에 한다([placeFieldsToPayload]).
 * 폼의 `''`·`'3'`·`3.7` 이 본문에서는 `null`·`3`·`3` 한 모양이 되기 때문에, 정규화 전에 비교하면
 * 아무것도 고치지 않은 폼이 「바뀌었다」고 답한다. 소유 파트너는 판정 입력이 아니라 뺀다.
 */
export const AUTO_ENROLL_INPUT_KEYS = Object.freeze(PLACE_CALLPOP_FIELD_KEYS.filter((key) => key !== 'autoEnrollYn'))

/** 폼·서버 값 → 자동 편입 입력만 남긴 저장 본문 모양 */
export function autoEnrollInputsOf(form) {
  const payload = placeFieldsToPayload(form)

  return AUTO_ENROLL_INPUT_KEYS.reduce((acc, key) => Object.assign(acc, { [key]: payload[key] }), {})
}

/**
 * 읽어 온 값과 지금 폼이 **자동 편입 입력에서** 다른가.
 *
 * 다르면 미리보기가 보여 주는 목록과 저장 뒤 실제 편입 대상이 갈린다 — 방금 지역을 넣었는데
 * 「운영지역이 설정되지 않아…」가 뜨는 것이 그 갈림의 가장 눈에 띄는 얼굴이다. 그래서 화면은
 * 켜기·실행을 열기 전에 이것을 묻고, 참이면 먼저 저장하라고 말한다.
 *
 * 두 값을 같은 함수([autoEnrollInputsOf])가 만든 **같은 키 순서**의 객체로 좁혀 놓았으므로 직렬화 비교로
 * 충분하다(키가 생기고 없어지는 축이 없다 — 그것이 [AUTO_ENROLL_INPUT_KEYS] 가 목록인 이유다).
 *
 * @param {?object} saved 서버에서 읽어 온 원본 스냅샷([placeFieldsFromServer] 의 답)
 * @param {?object} form 지금 폼
 */
export function autoEnrollInputsChanged(saved, form) {
  return JSON.stringify(autoEnrollInputsOf(saved)) !== JSON.stringify(autoEnrollInputsOf(form))
}

/** 표시 순서 — 표에 있는 사유가 먼저(서버 우선순위), 모르는 사유는 뒤에 온 순서대로 */
function orderedReasonCodes(codes) {
  const known = Object.keys(AUTO_ENROLL_REASON_NM)
  const rows = Array.isArray(codes) ? codes : []

  return known.filter((code) => rows.indexOf(code) > -1).concat(rows.filter((code) => known.indexOf(code) === -1))
}

/**
 * 두 값이 **같은 소유 파트너**를 가리키는가 — 「옮기는 저장인가」([ownerPartnerTransfers])와 「저장 뒤에도
 * 콜팝인가」([nextCallpopOwned])가 같은 비교를 두 번 적지 않게 한다.
 *
 * 빈 값의 세 모양([isOwnerPartnerEmpty])과 숫자·문자열 표기 차이(`258` ↔ `'258'`)를 한 답으로 접는다.
 */
function sameOwnerPartner(before, after) {
  if (isOwnerPartnerEmpty(before)) return isOwnerPartnerEmpty(after)
  if (isOwnerPartnerEmpty(after)) return false

  return Number(before) === Number(after)
}

/**
 * 문장이 이 매장 번호를 가리키는가 — **숫자 경계로** 본다(`1301` 이 `13011` 에 걸리지 않게).
 *
 * 번호가 숫자가 아니면 거짓이다(정규식에 그 값이 섞이는 자리를 만들지 않는다).
 */
function mentionsStoreCd(sentence, storeCd) {
  const cd = storeCd === null || storeCd === undefined ? '' : String(storeCd).trim()
  if (!/^\d+$/.test(cd)) return false

  return new RegExp(`(^|[^0-9])${cd}([^0-9]|$)`).test(sentence)
}

function joinName(sido, districts) {
  return [sido].concat(districts).filter(Boolean).join(' ')
}

function parseJsonObject(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null

  try {
    const parsed = JSON.parse(raw)

    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

// 숫자 읽기는 `utils/numberValue` 가 소유한다(파일 맨 위 import) — 종전의 사본은 공백뿐인 문자열을
// `0` 으로 읽어, 같은 좌표 칸이 어느 파일을 지나느냐에 따라 「없음」과 「0」으로 갈렸다

function toIntOrNull(value) {
  const num = toNumberOrNull(value)

  return num === null ? null : Math.trunc(num)
}
